'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import type { CallbackSettingsInput, FormLocale } from '@jlg-website/shared';
import { monthOf, shiftMonth } from '../shared/date-time';
import { SubmitError } from '../shared/submit-error';
import type { CompletionAction, ConfirmCallback } from '../shared/type';
import { useCallbackSubmit } from '../shared/use-callback-submit';
import { useVisitorTimeZone } from '../shared/use-visitor-time-zone';
import { listDaySlots, listMonthDays } from './business-hours';
import { ConfirmStep } from './confirm-step';
import { ContinueButton } from './continue-button';
import { DateStep } from './date-step';
import { StepProgress } from './step-progress';
import { SuccessStep } from './success-step';
import { TimeStep } from './time-step';
import { useConfirmCountdown } from './use-confirm-countdown';
import { slotPeriods } from './constants';
import type { CallbackSlot, SlotPeriod } from './type';

// Kept out of the component so an absent `takenAt` never changes identity between renders and
// busts the memos below — the month one walks every day of the calendar.
const NO_TAKEN_SLOTS: string[] = [];

// How often the picker notices that a slot just became past. A minute is finer than the
// smallest slot the settings allow, so nothing bookable lingers.
const CLOCK_REFRESH_MS = 60_000;

type ScheduleCallbackProps = {
  settings: CallbackSettingsInput;
  phone: string;
  /** ZIP code from the contact step — the zone the visitor reads their times in comes from it. */
  zipCode: string;
  /** Slots already booked by other visitors, as ISO instants. */
  takenAt?: string[];
  locale?: FormLocale;
  onConfirm?: ConfirmCallback;
  /**
   * The scheduler's opening question, shown above the calendar on the first step and gone from the
   * steps after it. The answer stays on screen, so the visitor can change it without going back.
   */
  prompt?: ReactNode;
  /** While false, a first step that carries a prompt shows only that — the calendar waits. */
  promptAnswered?: boolean;
  completionAction?: CompletionAction;
};

const firstPeriodWithSlots = (slots: CallbackSlot[]): SlotPeriod =>
  slotPeriods.find((period) => slots.some((slot) => slot.period === period && slot.available)) ??
  'morning';

export function ScheduleCallback({
  settings,
  phone,
  zipCode,
  takenAt = NO_TAKEN_SLOTS,
  locale = 'en',
  onConfirm,
  prompt,
  promptAnswered = false,
  completionAction,
}: ScheduleCallbackProps) {
  const [step, setStep] = useState(1);
  const [clock, setClock] = useState(() => new Date());
  const [browsedMonth, setBrowsedMonth] = useState<string | null>(null);
  const [chosenTimeZone, setChosenTimeZone] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CallbackSlot | null>(null);
  const [period, setPeriod] = useState<SlotPeriod>('morning');
  const [notes, setNotes] = useState('');
  const [scheduled, setScheduled] = useState(false);

  const { submitting, failed, submit } = useCallbackSubmit(onConfirm);

  // Slots that have just passed have to stop being offered while the visitor sits on the page,
  // otherwise they can book a time the endpoint will reject as no longer in the future.
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), CLOCK_REFRESH_MS);

    return () => clearInterval(timer);
  }, []);

  // The zone comes from the ZIP the visitor gave; the picker lets them correct it from there.
  const detectedTimeZone = useVisitorTimeZone(zipCode, settings);

  const timeZone = chosenTimeZone ?? detectedTimeZone;
  const month = browsedMonth ?? monthOf(clock);

  // A prompt gates the calendar behind itself; without one the picker is the whole first step.
  const calendarShown = !prompt || promptAnswered;

  /** The day's slots as of `now` — asked for on every clock tick, and again once one expires. */
  const daySlotsAt = useCallback(
    (date: string, now: Date) =>
      listDaySlots({ date, settings, visitorTimeZone: timeZone, now, takenAt }),
    [settings, timeZone, takenAt]
  );

  const days = useMemo(
    () => listMonthDays({ month, settings, visitorTimeZone: timeZone, now: clock, takenAt }),
    [clock, month, settings, timeZone, takenAt]
  );

  const slots = useMemo(
    () => (selectedDate ? daySlotsAt(selectedDate, clock) : []),
    [clock, selectedDate, daySlotsAt]
  );

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleChangeTimeZone = (nextTimeZone: string) => {
    setChosenTimeZone(nextTimeZone);
    setSelectedSlot(null);
  };

  const handleContinueFromDate = () => {
    setPeriod(firstPeriodWithSlots(slots));
    setStep(2);
  };

  /**
   * The picked slot came too close to starting for the endpoint to still accept it. The visitor
   * goes back to the last step that can still offer something: the times of the same day while
   * any remain, the calendar once the day is spent — both rebuilt from the refreshed clock, so
   * what they land on is what is actually left.
   */
  const handleConfirmDeadline = useCallback(() => {
    const now = new Date();

    setClock(now);
    setSelectedSlot(null);

    if (!selectedDate) return;

    const slotsLeft = daySlotsAt(selectedDate, now);

    if (slotsLeft.some((slot) => slot.available)) {
      setPeriod(firstPeriodWithSlots(slotsLeft));
      setStep(2);

      return;
    }

    setSelectedDate(null);
    setStep(1);
  }, [selectedDate, daySlotsAt]);

  // Only the confirmation step is on the clock: it is the one place the visitor can sit still
  // while the slot they picked runs out.
  const remainingMs = useConfirmCountdown(
    step === 3 && selectedSlot ? selectedSlot.startsAt : null,
    handleConfirmDeadline
  );

  const handleConfirm = async () => {
    if (!selectedSlot) return;

    const booked = await submit({ callback_on: selectedSlot.startsAt, tz: settings.tz, notes });

    if (booked) setScheduled(true);
  };

  if (scheduled && selectedDate && selectedSlot) {
    return (
      <div className="w-full">
        <SuccessStep
          date={selectedDate}
          slot={selectedSlot}
          timeZone={timeZone}
          phone={phone}
          locale={locale}
          completionAction={completionAction}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <StepProgress step={step} locale={locale} />

      {step === 1 && prompt}

      {step === 1 && calendarShown && (
        <DateStep
          month={month}
          days={days}
          selectedDate={selectedDate}
          timeZone={timeZone}
          locale={locale}
          canGoToPreviousMonth={month > monthOf(clock)}
          onSelectDate={handleSelectDate}
          onChangeTimeZone={handleChangeTimeZone}
          onChangeMonth={(offset) => setBrowsedMonth(shiftMonth(month, offset))}
        />
      )}

      {step === 2 && selectedDate && (
        <TimeStep
          date={selectedDate}
          slots={slots}
          period={period}
          selectedSlot={selectedSlot}
          locale={locale}
          onSelectPeriod={setPeriod}
          onSelectSlot={setSelectedSlot}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && selectedDate && selectedSlot && (
        <ConfirmStep
          date={selectedDate}
          slot={selectedSlot}
          timeZone={timeZone}
          phone={phone}
          notes={notes}
          locale={locale}
          remainingMs={remainingMs}
          submitting={submitting}
          onChangeNotes={setNotes}
          onChangeSchedule={() => setStep(2)}
          onBack={() => setStep(2)}
          onConfirm={handleConfirm}
        />
      )}

      {failed && <SubmitError locale={locale} />}

      {step === 1 && calendarShown && (
        <ContinueButton disabled={!selectedDate} locale={locale} onClick={handleContinueFromDate} />
      )}

      {step === 2 && (
        <ContinueButton disabled={!selectedSlot} locale={locale} onClick={() => setStep(3)} />
      )}
    </div>
  );
}

export default ScheduleCallback;
